import { EXIT_LEAD_FRAMES } from "./beats.js";
import { STYLE_BPM, type Scene, type VideoStyle } from "./scene.js";
import { HERO_RISE } from "./showreel/pieces/Hero.beats.js";

/**
 * How a video opens (the owner's decision, 2026-10-13): on its hook, in huge type that fills the phone-safe box, every
 * word of it landed on frame 0. Frame 0 is then the video's thumbnail everywhere (the app's poster, the cover Instagram
 * and TikTok are told to take, and what a feed shows before the video moves) and it is what stops the scroll. The words
 * are the first shot's own, never new ones, and drawn once: the poster is the shot's own heading, caption, line or
 * tour words, moved, and it settles into the place the shot draws them.
 *
 * A composition says it opens this way (`Composition.opening`), written by `compose` on every new video. One without it
 * was made before and renders, is covered and sounds exactly as it did: its hits are already in its stored WAV.
 *
 * The timing, in frames from the first shot's start:
 * - `0` to `hold`: the poster, on one beat of the style's music (`STYLE_BPM`, Bold half a second, Storybook three
 *   quarters), so a feed's autoplay shows the giant hook long enough to read before anything moves. It is pushed in a
 *   little, the style's ground moving behind it.
 * - `hold` to `end`: the settle, in the style's own move (`Look.settle` in the scene kit), `HOOK_SETTLE` frames: the
 *   poster shrinks as one block onto the scene's heading, its lines still stacked and, over its last frames, already
 *   sliding sideways to where they rejoin; then they close up into the scene's lines.
 * - From `end`: everything else the shot draws builds in on its own beats, timed on what is left of the shot.
 */
export const OPENING_HOOK = "hook" as const;

/**
 * Frames the hook takes to settle from its poster into the scene, by style: quick where the style is loud (Bold, Neon),
 * slower where it is calm (Storybook's float, Retro's dropped type). A showreel piece settles its own way (its rows rise
 * to make room for the payoff, `heroTiming`), in `HERO_RISE` frames.
 */
export const HOOK_SETTLE: Record<VideoStyle, number> = { clean: 16, bold: 12, storybook: 22, notebook: 16, neon: 12, retro: 20, showreel: HERO_RISE, editorial: HERO_RISE, poster: HERO_RISE, handmade: HERO_RISE };

/** The fewest frames the poster holds, however short its shot. */
const HOLD_LEAST = 6;
/** Frames kept after the settle for the shot's own words to be read before they start to leave. */
const READ_LEAST = 12;

export type HookTiming = { hold: number; settle: number; end: number };

/**
 * When the hook holds and settles in a first shot of `frames` frames in `style` (`OPENING_HOOK`): a beat of the style's
 * music and the style's own settle, in whole frames. A shot too short for both and `READ_LEAST` before its words start
 * to leave (`EXIT_LEAD_FRAMES`), under about a second and a half in Bold and two in Storybook, has them drawn shorter
 * together, the poster never under `HOLD_LEAST` frames, so its words are still read before they leave. A pictures video
 * has no style and takes Clean's, as its music does.
 */
export function hookTiming(style: VideoStyle | undefined, frames = Infinity, fps = 30): HookTiming {
  const s = style ?? "clean";
  let hold = Math.round((fps * 60) / STYLE_BPM[s]), settle = HOOK_SETTLE[s];
  const room = frames - READ_LEAST - EXIT_LEAD_FRAMES;
  if (hold + settle > room) {
    const k = Math.max(0, room) / (hold + settle);
    hold = Math.max(HOLD_LEAST, Math.round(hold * k));
    settle = Math.max(4, Math.round(settle * k));
  }
  return { hold, settle, end: hold + settle };
}

/**
 * A first shot as its hook is read: its scene, or a picture's own line and its tour's stops, as a stored composition
 * has them; `compose` asks it of the script's shot in the same shape (`hookless`), so the two ask the same question.
 */
export type HookShot = {
  scene?: Scene | null | undefined;
  text?: ReadonlyArray<{ text: string }> | undefined;
  motion?: { kind: string; stops?: ReadonlyArray<{ effect: string; say: string | null }> | undefined } | undefined;
};

/**
 * The words a first shot opens on as its hook, as the script wrote them: a card's heading, a figure's number with the
 * words that go with it, a painted scene's caption, a showreel piece's opening words (a hero's display lines, its payoff
 * rising later; a figure piece's whole statement, every word of which its poster lands, `pieceHook`; any other piece's
 * first line), a picture's on-screen line, or a toured photo's first stop that says something. Null for a picture
 * with none: `compose` then gives it its first spoken line, cut where it can stop (`hookless`), and with nothing said
 * either it opens on the photo itself.
 */
export function hookOf(s: HookShot): string | null {
  const scene = s.scene;
  if (scene) {
    switch (scene.kind) {
      case "headline": case "document_check": case "steps": case "compare": case "offer": return squeeze(scene.headline);
      case "big_number": return squeeze(`${scene.prefix}${scene.value}${/[a-z]{2,}/i.test(scene.suffix) ? ` ${scene.suffix}` : scene.suffix}`);
      case "painted": return squeeze(scene.caption);
      case "showreel": {
        const lines = scene.lines.map(squeeze).filter(Boolean), figure = scene.figure ? `${scene.figure.prefix}${scene.figure.value}${scene.figure.suffix}` : null;
        // Its figure where the piece draws it: after the line above when there are two lines or more, else before its
        // caption (`figureText`). Without a figure its heavy line is one of its lines, and they are all the statement.
        if (scene.piece === "figure") {
          const at = lines.length >= 2 ? 1 : 0;
          return [...lines.slice(0, at), ...(figure ? [figure] : []), ...lines.slice(at)].join(" ") || null;
        }
        return (scene.piece === "hero" && lines.length > 1 ? lines.slice(0, -1) : lines.slice(0, 1)).join(" ") || null;
      }
    }
  }
  const line = squeeze(s.text?.[0]?.text);
  if (line) return line;
  const stop = s.motion?.kind === "tour" ? s.motion.stops?.[0] : undefined;
  return stop?.say && stop.effect !== "flag" ? squeeze(stop.say) || null : null;
}

const squeeze = (t: string | null | undefined) => (t ?? "").replace(/\s+/g, " ").trim();
/**
 * A text's words as two texts are compared: lower case, letters and digits, a figure's thousands ungrouped, so 1,284
 * is 1284 (a figure card's hook is its value as stored, a voice's words the script's).
 */
const plain = (t: string) => t.toLowerCase().replace(/(\d),(?=\d{3}(?!\d))/g, "$1").match(/[\p{L}\p{N}]+/gu) ?? [];

/**
 * Whether a caption's phrase says the hook again: at least half of its words are the hook's. While the first shot is on
 * the hook is on screen in big type, then as the shot's heading, and a chip under it saying the same words is the same
 * thing twice; one that says something else is still shown, so a narrated video's first words stay captioned.
 */
export function saysHook(phrase: ReadonlyArray<string>, hook: string | null): boolean {
  if (!hook) return false;
  const words = phrase.flatMap(plain), own = new Set(plain(hook));
  return words.length > 0 && words.filter((w) => own.has(w)).length * 2 >= words.length;
}
